import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { EstablishmentQuestion } from '@/lib/establishmentQuestionUtils';

interface EstablishmentQuestionFieldProps {
  question: EstablishmentQuestion | null;
  answers: string[];
  onAnswersChange: (answers: string[]) => void;
  compact?: boolean;
  idPrefix?: string;
  /**
   * Contexte équipe (tableau de bord) : la réponse reste facultative même si la
   * question est obligatoire pour les clients, et peut être effacée.
   */
  staffMode?: boolean;
}

/**
 * Question posée au client dans le formulaire de réservation.
 * Texte brut uniquement (jamais de HTML injecté).
 */
export function EstablishmentQuestionField({
  question,
  answers,
  onAnswersChange,
  compact = false,
  idPrefix = 'question',
  staffMode = false,
}: EstablishmentQuestionFieldProps) {
  if (!question) return null;

  const options = question.options.filter((o) => o.trim() !== '');
  if (options.length === 0) return null;

  const textSize = compact ? 'text-xs' : 'text-sm';

  const toggle = (option: string, checked: boolean) => {
    if (checked) {
      onAnswersChange(Array.from(new Set([...answers, option])));
    } else {
      onAnswersChange(answers.filter((a) => a !== option));
    }
  };

  return (
    <div className="space-y-2 rounded-lg border p-3">
      <div className="flex items-start justify-between gap-2">
        <Label className={`font-medium ${compact ? 'text-xs' : 'text-sm'}`}>
          <span className="whitespace-pre-wrap">{question.question}</span>
          {question.isRequired && !staffMode && <span className="text-destructive"> *</span>}
        </Label>
        {staffMode && answers.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs shrink-0"
            onClick={() => onAnswersChange([])}
          >
            Effacer
          </Button>
        )}
      </div>
      {staffMode && (
        <p className="text-xs text-muted-foreground">Facultatif côté équipe.</p>
      )}

      {question.allowMultiple ? (
        <div className="space-y-2 pt-1">
          {options.map((option, index) => {
            const id = `${idPrefix}-${question.id}-${index}`;
            return (
              <div key={id} className="flex items-start space-x-2">
                <Checkbox
                  id={id}
                  checked={answers.includes(option)}
                  onCheckedChange={(checked) => toggle(option, checked === true)}
                  className="mt-0.5"
                />
                <Label htmlFor={id} className={`font-normal cursor-pointer leading-tight ${textSize}`}>
                  {option}
                </Label>
              </div>
            );
          })}
        </div>
      ) : (
        <RadioGroup
          value={answers[0] ?? ''}
          onValueChange={(value) => onAnswersChange(value ? [value] : [])}
          className="space-y-2 pt-1"
        >
          {options.map((option, index) => {
            const id = `${idPrefix}-${question.id}-${index}`;
            return (
              <div key={id} className="flex items-start space-x-2">
                <RadioGroupItem value={option} id={id} className="mt-0.5" />
                <Label htmlFor={id} className={`font-normal cursor-pointer leading-tight ${textSize}`}>
                  {option}
                </Label>
              </div>
            );
          })}
        </RadioGroup>
      )}
    </div>
  );
}
